import { useContext, useState, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';
import { AssetsSchema, AssetsType } from '../../types';
import { styles } from '../../../../styles';
import { FinancialStatusContext } from '../../context/financial-status-context';
import Button from '../../../../_sharedComponents/button';
import { formatter } from '../../../../../_utils/format';
import StepHeader from '../../step-header';
import ErrorMessage from '../error-message';
import Label from '../label';

export default function Assets() {
	const financialContext = useContext(FinancialStatusContext);
	const [totalAssets, setTotalAssets] = useState(0);
	const {
		handleSubmit,
		control,
		watch,
		setValue,
		formState: { errors },
	} = useForm<AssetsType>({
		defaultValues: {
			cashValue: 0,
			propertyValue: 0,
			vehicleValue: 0,
			makeAndModel: '',
			retirementValue: 0,
			stockValue: 0,
			otherItemsValue: 0,
			totalAssets: 0,
		},
		shouldUnregister: false,
		resolver: zodResolver(AssetsSchema),
	});

	const onSubmit = (data: AssetsType) => {
		financialContext.updateAssets(data);
		financialContext.updateCurrentStep(6);
		scrollTo({ top: 0, behavior: 'smooth' });
	};

	useEffect(() => {
		// Calculate total employment income
		const cashValue = watch('cashValue');
		const propertyValue = watch('propertyValue');
		const vehicleValue = watch('vehicleValue');
		const retirementValue = watch('retirementValue');
		const stockValue = watch('stockValue');
		const otherItemsValue = watch('otherItemsValue');

		const total = cashValue + propertyValue + vehicleValue + retirementValue + stockValue + otherItemsValue;
		setTotalAssets(total);
		setValue('totalAssets', total);
	}, [
		watch('cashValue'),
		watch('propertyValue'),
		watch('vehicleValue'),
		watch('retirementValue'),
		watch('stockValue'),
		watch('otherItemsValue'),
	]);

	return (
		<form className="h-full" onSubmit={handleSubmit(onSubmit)}>
			<div className="flex h-full flex-col justify-between">
				<StepHeader step={5} />
				<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-20 lg:space-y-0">
					{/* cashValue */}
					<div className="min-h-24 ">
						<Label currentStep="assets" value="cashValue" />
						<Controller
							control={control}
							name="cashValue"
							render={({ field }) => (
								<input
									{...field}
									className={`${styles.mediumInput} ${errors.cashValue ? styles.error : ''}`}
									id="cashValue"
									onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
									placeholder={financialContext.getPlaceholder({ step: 'assets', value: 'cashValue' })}
									type="number"
									value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
								/>
							)}
						/>
						{errors.cashValue ? (
							<div className="md:max-w-56">
								<ErrorMessage currentStep="assets" value="cashValue" />
							</div>
						) : null}
					</div>
				</div>
				<div className="mb-5 text-base font-light text-gray-500">
					Total value of land, house(s), or real property in your name (include assets held for your benefit)
				</div>
				<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-20 lg:space-y-0">
					{/* Total Property Value */}
					<div className="min-h-24 ">
						<Label currentStep="assets" value="propertyValue" />
						<Controller
							control={control}
							name="propertyValue"
							render={({ field }) => (
								<input
									{...field}
									className={`${styles.mediumInput} ${errors.propertyValue ? styles.error : ''}`}
									id="propertyValue"
									onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
									placeholder={financialContext.getPlaceholder({ step: 'assets', value: 'propertyValue' })}
									type="number"
									value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
								/>
							)}
						/>
						{errors.propertyValue ? (
							<div className="md:max-w-56">
								<ErrorMessage currentStep="assets" value="propertyValue" />
							</div>
						) : null}
					</div>
				</div>
				<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-20 lg:space-y-0">
					{/* Market Value of Vehicles */}
					<div className="min-h-24 ">
						<Label currentStep="assets" value="vehicleValue" />
						<Controller
							control={control}
							name="vehicleValue"
							render={({ field }) => (
								<input
									{...field}
									className={`${styles.mediumInput} ${errors.vehicleValue ? styles.error : ''}`}
									id="vehicleValue"
									onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
									placeholder={financialContext.getPlaceholder({ step: 'assets', value: 'vehicleValue' })}
									type="number"
									value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
								/>
							)}
						/>
						{errors.vehicleValue ? (
							<div className="md:max-w-56">
								<ErrorMessage currentStep="assets" value="vehicleValue" />
							</div>
						) : null}
					</div>
					{/* Make / Model */}
					<div className="min-h-24 ">
						<Label currentStep="assets" value="makeAndModel" />
						<Controller
							control={control}
							name="makeAndModel"
							render={({ field }) => (
								<input
									{...field}
									className={`${styles.mediumInput} ${errors.makeAndModel ? styles.error : ''}`}
									id="makeAndModel"
									placeholder={financialContext.getPlaceholder({ step: 'assets', value: 'makeAndModel' })}
									type="text"
								/>
							)}
						/>
						{errors.makeAndModel ? (
							<div className="md:max-w-56">
								<ErrorMessage currentStep="assets" value="makeAndModel" />
							</div>
						) : null}
					</div>
				</div>

				<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-20 lg:space-y-0">
					{/* Retirement Accounts */}
					<div className="min-h-24 ">
						<Label currentStep="assets" value="retirementValue" />
						<Controller
							control={control}
							name="retirementValue"
							render={({ field }) => (
								<input
									{...field}
									className={`${styles.mediumInput} ${errors.retirementValue ? styles.error : ''}`}
									id="retirementValue"
									onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
									placeholder={financialContext.getPlaceholder({ step: 'assets', value: 'retirementValue' })}
									type="number"
									value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
								/>
							)}
						/>
						{errors.retirementValue ? (
							<div className="md:max-w-56">
								<ErrorMessage currentStep="assets" value="retirementValue" />
							</div>
						) : null}
					</div>
					{/* Make / Model */}
					<div className="min-h-24 ">
						<Label currentStep="assets" value="stockValue" />
						<Controller
							control={control}
							name="stockValue"
							render={({ field }) => (
								<input
									{...field}
									className={`${styles.mediumInput} ${errors.stockValue ? styles.error : ''}`}
									id="stockValue"
									onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
									placeholder={financialContext.getPlaceholder({ step: 'assets', value: 'stockValue' })}
									type="number"
									value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
								/>
							)}
						/>
						{errors.stockValue ? (
							<div className="md:max-w-56">
								<ErrorMessage currentStep="assets" value="stockValue" />
							</div>
						) : null}
					</div>
				</div>

				<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-20 lg:space-y-0">
					{/* Other Items */}
					<div>
						<Label currentStep="assets" value="otherItemsValue" />
						<Controller
							control={control}
							name="otherItemsValue"
							render={({ field }) => (
								<input
									{...field}
									className={`${styles.mediumInput} ${errors.otherItemsValue ? styles.error : ''}`}
									id="otherItemsValue"
									onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
									placeholder={financialContext.getPlaceholder({ step: 'assets', value: 'otherItemsValue' })}
									type="number"
									value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
								/>
							)}
						/>
						{errors.otherItemsValue ? (
							<div className="md:max-w-56">
								<ErrorMessage currentStep="assets" value="otherItemsValue" />
							</div>
						) : null}
					</div>
				</div>
				<div className="mb-10 flex justify-end space-y-3 md:min-h-24 md:w-3/4">
					<div className="w-full justify-between font-light text-gray-500 lg:flex lg:w-1/2">
						<div>Total estimated assets:</div>
						<div className="font-semibold">{formatter.format(totalAssets)}</div>
					</div>
				</div>

				<div className="mx-auto my-10 flex space-x-10">
					<Button
						label={financialContext.language === 'en' ? 'Prev' : 'Anterior'}
						onClick={() => {
							financialContext.updateCurrentStep(4);
							scrollTo({ top: 0, behavior: 'smooth' });
						}}
						type="button"
					/>

					<Button label={financialContext.language === 'en' ? 'Next' : 'Seguiente'} type="submit" />
				</div>
			</div>
		</form>
	);
}
